/**
 * UnifiedCalendarView.js - Enterprise-level full calendar view
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import {
    Box, Typography, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, Paper, Tooltip, IconButton, Skeleton, Fade,
    FormControl, Select, MenuItem, InputLabel, Avatar, Stack, Divider
} from '@mui/material';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import axiosInstance from '../../services/axiosInstance';
import apiEndPointsConfig from '../../services/apiEndPoints';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

const GOOGLE_ICON = 'https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg';
const OUTLOOK_ICON = 'https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg';

const STATUS_COLORS = {
    confirmed: { bg: '#10b981', light: '#d1fae5', text: '#065f46' },
    pending: { bg: '#f59e0b', light: '#fef3c7', text: '#92400e' },
    cancelled: { bg: '#ef4444', light: '#fee2e2', text: '#991b1b' },
    completed: { bg: '#6366f1', light: '#ede9fe', text: '#4c1d95' },
    'no-show': { bg: '#94a3b8', light: '#f1f5f9', text: '#475569' },
};

const getStatusConfig = (status) => STATUS_COLORS[status] || { bg: '#3b82f6', light: '#dbeafe', text: '#1e40af' };

const getEventStyle = (event) => {
    const cfg = getStatusConfig(event.resource?.status);
    return {
        style: {
            background: `linear-gradient(135deg, ${cfg.bg}ee, ${cfg.bg}bb)`,
            border: 'none', borderRadius: '6px', color: 'white',
            fontSize: '11px', fontWeight: 600, padding: '2px 6px',
            boxShadow: `0 2px 8px ${cfg.bg}44`,
        },
    };
};

const CustomEvent = ({ event }) => {
    const appt = event.resource;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', overflow: 'hidden' }}>
            <Typography variant="caption" noWrap sx={{ flex: 1, fontWeight: 700, color: 'white', fontSize: '11px' }}>
                {event.title}
            </Typography>
            {appt?.googleEventId && <img src={GOOGLE_ICON} width="9" alt="G" />}
            {appt?.outlookEventId && <img src={OUTLOOK_ICON} width="9" alt="O" />}
            {appt?.meetingLink && <VideoCallIcon sx={{ fontSize: 11, color: 'white' }} />}
        </Box>
    );
};

const InfoRow = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <Box sx={{ color: '#64748b', mt: 0.1, flexShrink: 0 }}>{icon}</Box>
        <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.2 }}>{label}</Typography>
            <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>{value}</Typography>
        </Box>
    </Box>
);

const AppointmentDetailDialog = ({ event, open, onClose, onCancel }) => {
    if (!event) return null;
    const appt = event.resource;
    const cfg = getStatusConfig(appt?.status);
    const clientName = appt?.guestDetails?.name || appt?.client?.name || 'Unknown';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
            <DialogTitle sx={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2.5, px: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: `${cfg.bg}33`, border: `1px solid ${cfg.bg}66`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <EventIcon sx={{ color: cfg.bg, fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>{event.title}</Typography>
                        <Chip label={appt?.status || 'confirmed'} size="small" sx={{ bgcolor: `${cfg.bg}33`, color: cfg.bg, border: `1px solid ${cfg.bg}55`, fontWeight: 700, fontSize: '0.65rem', height: 18, mt: 0.3 }} />
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5, pb: 2, px: 3, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <InfoRow icon={<PersonIcon fontSize="small" />} label="Client" value={clientName} />
                    <InfoRow icon={<AccessTimeIcon fontSize="small" />} label="Appointment Time" value={`${moment(event.start).format('MMM D, YYYY · h:mm A')} – ${moment(event.end).format('h:mm A')}`} />
                    {appt?.meetingLink && (
                        <Box sx={{ p: 1.5, borderRadius: 2, background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1px solid #93c5fd', display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <VideoCallIcon sx={{ color: '#2563eb', fontSize: 22, flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">Video Meeting</Typography>
                                <Typography component="a" href={appt.meetingLink} target="_blank" rel="noopener" variant="body2" sx={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>Join Meeting →</Typography>
                            </Box>
                        </Box>
                    )}
                    {(appt?.googleEventId || appt?.outlookEventId) && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {appt?.googleEventId && <Chip icon={<img src={GOOGLE_ICON} width="12" alt="G" />} label="Google Synced" size="small" sx={{ bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac', fontWeight: 600, fontSize: '0.7rem' }} />}
                            {appt?.outlookEventId && <Chip icon={<img src={OUTLOOK_ICON} width="12" alt="O" />} label="Outlook Synced" size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd', fontWeight: 600, fontSize: '0.7rem' }} />}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1, bgcolor: '#fff' }}>
                {appt?.status !== 'cancelled' && (
                    <Button variant="outlined" color="error" size="small" onClick={() => onCancel(appt._id)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                        Cancel Appointment
                    </Button>
                )}
                <Button onClick={onClose} variant="contained" size="small" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, ml: 'auto', background: 'linear-gradient(135deg, #1565c0, #1976d2)', boxShadow: '0 4px 14px #1565c033' }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const CustomToolbar = ({ label, onNavigate, onView, view }) => {
    const views = [
        { key: Views.MONTH, label: 'Month' },
        { key: Views.WEEK, label: 'Week' },
        { key: Views.DAY, label: 'Day' },
        { key: Views.AGENDA, label: 'Agenda' },
    ];
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, px: 2, py: 1.8, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" onClick={() => onNavigate('PREV')} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, width: 30, height: 30 }}>
                    <Typography sx={{ fontSize: 16, color: '#64748b', lineHeight: 1 }}>‹</Typography>
                </IconButton>
                <Button size="small" onClick={() => onNavigate('TODAY')} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: '#475569', bgcolor: '#f1f5f9', borderRadius: 1.5, px: 1.5, minWidth: 'auto', '&:hover': { bgcolor: '#e2e8f0' } }}>
                    Today
                </Button>
                <IconButton size="small" onClick={() => onNavigate('NEXT')} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, width: 30, height: 30 }}>
                    <Typography sx={{ fontSize: 16, color: '#64748b', lineHeight: 1 }}>›</Typography>
                </IconButton>
                <Typography variant="subtitle1" fontWeight={700} sx={{ ml: 1, color: '#0f172a' }}>{label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#f1f5f9', borderRadius: 2, p: 0.4 }}>
                {views.map(v => (
                    <Button key={v.key} size="small" onClick={() => onView(v.key)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.74rem', borderRadius: 1.5, px: 1.5, minWidth: 'auto', color: view === v.key ? '#fff' : '#64748b', bgcolor: view === v.key ? '#1565c0' : 'transparent', boxShadow: view === v.key ? '0 2px 8px #1565c044' : 'none', transition: 'all 0.2s', '&:hover': { bgcolor: view === v.key ? '#1976d2' : '#e2e8f0' } }}>
                        {v.label}
                    </Button>
                ))}
            </Box>
        </Box>
    );
};

const UnifiedCalendarView = ({ activeTab }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentView, setCurrentView] = useState(Views.MONTH);
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(localStorage.getItem('userId') || '');
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchTeamMembers = useCallback(async () => {
        try {
            const clientId = localStorage.getItem('userId');
            if (!clientId) return;

            const res = await axiosInstance.get(`${apiEndPointsConfig.getTeamMembersByClientId}/${clientId}`);
            if (res.data.success) {
                setTeamMembers(res.data.data || []);
            }
        } catch (err) {
            console.error('Failed to load team members:', err);
        }
    }, []);

    const fetchAppointments = useCallback(async (userIdOverride) => {
        const targetUserId = userIdOverride || selectedUserId;
        if (!targetUserId) return;

        setLoading(true);
        setIsRefreshing(true);
        try {
            console.log(`[Calendar] Fetching for user: ${targetUserId}`);
            const res = await axiosInstance.get('/appointments', {
                params: {
                    userId: targetUserId,
                    limit: 1000,
                    sortBy: 'startTime',
                    sortOrder: '1'
                }
            });
            if (res.data.success) {
                console.log(`[Calendar] Received ${res.data.appointments.length} appointments`);
                const mapped = res.data.appointments.map(appt => {
                    const guestName = appt.guestDetails?.name || appt.client?.name || 'Guest';
                    return {
                        id: appt._id,
                        title: (appt.title && appt.title !== 'Appointment') ? `${guestName}: ${appt.title}` : guestName,
                        start: new Date(appt.startTime),
                        end: new Date(appt.endTime),
                        resource: appt,
                    };
                });
                setEvents(mapped);
            }
        } catch (err) {
            console.error('Failed to load appointments for calendar:', err);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [selectedUserId]);

    useEffect(() => {
        fetchTeamMembers();
    }, [fetchTeamMembers]);

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);

    // Refresh when tab becomes active
    useEffect(() => {
        if (activeTab === 1) {
            fetchAppointments();
        }
    }, [activeTab, fetchAppointments]);

    const handleUserChange = (event) => {
        const newUserId = event.target.value;
        setSelectedUserId(newUserId);
    };

    const handleCancelAppointment = async (appointmentId) => {
        try {
            await axiosInstance.put('/appointments/status', { appointmentId, status: 'cancelled' });
            setSelectedEvent(null);
            fetchAppointments();
        } catch (err) {
            console.error('Cancel failed:', err);
        }
    };

    const currentUserId = localStorage.getItem('userId');

    return (
        <Box>
            {/* Legend + Refresh */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem' }}>
                        Status
                    </Typography>
                    {Object.entries(STATUS_COLORS).map(([status, cfg]) => (
                        <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: cfg.bg }} />
                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'capitalize', fontSize: '0.72rem' }}>{status}</Typography>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* User Selection Filter */}
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                            value={selectedUserId}
                            onChange={handleUserChange}
                            displayEmpty
                            sx={{
                                borderRadius: 2,
                                bgcolor: '#fff',
                                fontSize: '0.85rem',
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 0.8
                                }
                            }}
                            renderValue={(selected) => {
                                if (selected === currentUserId) return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                                        <Typography variant="body2" fontWeight={600}>My Calendar</Typography>
                                    </Box>
                                );
                                const user = teamMembers.find(m => m._id === selected);
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar sx={{ width: 18, height: 18, fontSize: '10px', bgcolor: '#6366f1' }}>{user?.name?.charAt(0)}</Avatar>
                                        <Typography variant="body2" fontWeight={600}>{user?.name || 'Select User'}</Typography>
                                    </Box>
                                );
                            }}
                        >
                            <MenuItem value={currentUserId}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                    <Typography variant="body2">My Calendar</Typography>
                                </Box>
                            </MenuItem>
                            {teamMembers.length > 0 && <Divider sx={{ my: 0.5 }} />}
                            {teamMembers.map((member) => (
                                <MenuItem key={member._id} value={member._id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar sx={{ width: 20, height: 20, fontSize: '11px', bgcolor: '#eef2ff', color: '#6366f1' }}>{member.name.charAt(0)}</Avatar>
                                        <Typography variant="body2">{member.name}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={GOOGLE_ICON} width="13" alt="G" />
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem' }}>Google</Typography>
                        <img src={OUTLOOK_ICON} width="13" alt="O" style={{ marginLeft: 4 }} />
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem' }}>Outlook</Typography>
                    </Box>
                    <Tooltip title="Refresh Data" arrow>
                        <IconButton
                            size="small"
                            onClick={() => fetchAppointments()}
                            disabled={loading}
                            sx={{
                                bgcolor: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                width: 34,
                                height: 34,
                                '&:hover': { bgcolor: '#e2e8f0' },
                                transition: 'all 0.2s',
                                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }}
                        >
                            <RefreshIcon sx={{ fontSize: 18, color: '#475569' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {loading ? (
                <Box>
                    <Skeleton variant="rounded" height={60} sx={{ borderRadius: 2, mb: 1 }} />
                    <Skeleton variant="rounded" height={520} sx={{ borderRadius: 3 }} />
                </Box>
            ) : (
                <Fade in timeout={400}>
                    <Paper elevation={0} sx={{
                        border: '1px solid #e2e8f0', borderRadius: 2.5, overflow: 'hidden',
                        '& .rbc-header': { py: 1, fontSize: '0.75rem', fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
                        '& .rbc-today': { bgcolor: '#eff6ff !important' },
                        '& .rbc-off-range-bg': { bgcolor: '#fafafa' },
                        '& .rbc-date-cell': { fontSize: '0.8rem', color: '#334155' },
                        '& .rbc-date-cell.rbc-now': { fontWeight: 700, color: '#1565c0' },
                        '& .rbc-show-more': { color: '#1565c0', fontWeight: 600, fontSize: '0.72rem' },
                    }}>
                        <Calendar
                            localizer={localizer}
                            events={events}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ height: 600 }}
                            views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
                            defaultView={Views.MONTH}
                            date={currentDate}
                            view={currentView}
                            onNavigate={setCurrentDate}
                            onView={setCurrentView}
                            eventPropGetter={getEventStyle}
                            components={{ event: CustomEvent, toolbar: CustomToolbar }}
                            onSelectEvent={(event) => setSelectedEvent(event)}
                            popup
                            selectable={false}
                        />
                    </Paper>
                </Fade>
            )}

            <AppointmentDetailDialog
                event={selectedEvent}
                open={!!selectedEvent}
                onClose={() => setSelectedEvent(null)}
                onCancel={handleCancelAppointment}
            />
        </Box>
    );
};

export default UnifiedCalendarView;